import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import AdminLayout from "../components/AdminLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { 
  Plus, Pencil, MapPin, Plane, Ship, Building, Hotel, Landmark,
  RefreshCw, Search, X, Save, AlertCircle, ArrowUpDown
} from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';

const LOCATION_TYPES = [
  { value: "airport", label: "Airport", icon: Plane },
  { value: "city", label: "City", icon: Building },
  { value: "hotel", label: "Hotel", icon: Hotel },
  { value: "attraction", label: "Attraction", icon: Landmark },
  { value: "port", label: "Port", icon: Ship },
];

const REGIONS = [
  "Messinia",
  "Laconia", 
  "Arcadia",
  "Argolida",
  "Corinthia",
  "Achaea",
  "Ilia",
  "Attica"
];

export default function AdminLocationsPage() {
  const navigate = useNavigate();
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [regionFilter, setRegionFilter] = useState("all");
  const [sortBy, setSortBy] = useState("name"); // name, region, type
  const [sortOrder, setSortOrder] = useState("asc");
  
  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");
  
  // Form data
  const [formData, setFormData] = useState({
    slug: "",
    name: "",
    subtitle: "",
    type: "city",
    region: "Messinia"
  });

  useEffect(() => {
    const token = localStorage.getItem("adminToken");
    if (!token) {
      navigate("/admin/login");
      return;
    }
    fetchLocations();
  }, [navigate]);

  const fetchLocations = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API_BASE}/locations`);
      if (!res.ok) throw new Error("Failed to fetch locations");
      const data = await res.json();
      setLocations(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditingLocation(null);
    setFormData({
      slug: "",
      name: "",
      subtitle: "",
      type: "city",
      region: "Messinia"
    });
    setFormError("");
    setShowModal(true);
  };

  const openEditModal = (location) => {
    setEditingLocation(location);
    setFormData({
      slug: location.slug,
      name: location.name,
      subtitle: location.subtitle || "",
      type: location.type || "city",
      region: location.region || "Messinia"
    });
    setFormError("");
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem("adminToken");
    setSaving(true);
    setFormError("");

    try {
      const payload = {
        ...formData,
        subtitle: formData.subtitle || null
      };

      const url = editingLocation 
        ? `${API_BASE}/locations/${editingLocation.slug}`
        : `${API_BASE}/locations`;
      
      const method = editingLocation ? "PUT" : "POST";

      const res = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || "Failed to save location");
      }

      await fetchLocations();
      setShowModal(false);
    } catch (err) {
      setFormError(err.message);
    } finally {
      setSaving(false);
    }
  };

  // Get unique regions from data
  const availableRegions = [...new Set(locations.map(l => l.region).filter(Boolean))].sort();

  // Filter and sort locations
  const filteredLocations = locations
    .filter(loc => {
      const matchesSearch = !searchTerm || 
        loc.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        loc.slug.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesType = typeFilter === "all" || loc.type === typeFilter;
      const matchesRegion = regionFilter === "all" || loc.region === regionFilter;
      return matchesSearch && matchesType && matchesRegion;
    })
    .sort((a, b) => {
      let comparison = 0;
      if (sortBy === "name") {
        comparison = (a.name || "").localeCompare(b.name || "");
      } else if (sortBy === "region") {
        comparison = (a.region || "").localeCompare(b.region || "");
      } else if (sortBy === "type") {
        comparison = (a.type || "").localeCompare(b.type || "");
      }
      return sortOrder === "asc" ? comparison : -comparison;
    });

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(column);
      setSortOrder("asc");
    }
  };

  const getTypeIcon = (type) => {
    const found = LOCATION_TYPES.find(t => t.value === type);
    const Icon = found?.icon || MapPin;
    return <Icon className="w-4 h-4" />;
  };

  const getTypeBadge = (type) => {
    const colors = {
      airport: "bg-blue-100 text-blue-700",
      port: "bg-cyan-100 text-cyan-700",
      city: "bg-purple-100 text-purple-700",
      hotel: "bg-emerald-100 text-emerald-700",
      attraction: "bg-amber-100 text-amber-700"
    };
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${colors[type] || "bg-gray-100 text-gray-700"}`}>
        {getTypeIcon(type)}
        {type}
      </span>
    );
  };

  const getRegionBadge = (region) => {
    const colors = {
      'Messinia': 'bg-emerald-50 text-emerald-700',
      'Laconia': 'bg-purple-50 text-purple-700',
      'Arcadia': 'bg-blue-50 text-blue-700',
      'Argolida': 'bg-amber-50 text-amber-700',
      'Corinthia': 'bg-rose-50 text-rose-700',
      'Achaea': 'bg-cyan-50 text-cyan-700',
      'Ilia': 'bg-orange-50 text-orange-700',
      'Attica': 'bg-slate-100 text-slate-700'
    };
    return region ? (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${colors[region] || "bg-gray-100 text-gray-700"}`}>
        {region}
      </span>
    ) : (
      <span className="text-slate-400">—</span>
    );
  };

  const SortableHeader = ({ column, label }) => (
    <th 
      className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider cursor-pointer hover:bg-slate-100 select-none"
      onClick={() => handleSort(column)}
    >
      <div className="flex items-center gap-1">
        {label}
        <ArrowUpDown className={`w-3 h-3 ${sortBy === column ? 'text-blue-500' : 'text-slate-300'}`} />
      </div>
    </th>
  );

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-locations">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Locations</h1>
            <p className="text-slate-500">Manage pickup and dropoff locations ({filteredLocations.length} of {locations.length})</p>
          </div>
          <Button onClick={openCreateModal} data-testid="add-location-btn">
            <Plus className="w-4 h-4 mr-2" />
            Add Location
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {LOCATION_TYPES.map(t => {
            const count = locations.filter(l => l.type === t.value).length;
            const Icon = t.icon;
            return (
              <div key={t.value} className="bg-white rounded-xl border border-slate-200 p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center text-slate-600">
                    <Icon className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-slate-900">{count}</div>
                    <div className="text-xs text-slate-500">{t.label}s</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Search locations..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                data-testid="search-input"
              />
            </div>
            
            <div className="flex items-center gap-2">
              <label className="text-sm text-slate-600">Type:</label>
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className="text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white"
                data-testid="type-filter"
              >
                <option value="all">All Types</option>
                {LOCATION_TYPES.map(t => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>

            <div className="flex items-center gap-2">
              <label className="text-sm text-slate-600">Region:</label>
              <select
                value={regionFilter}
                onChange={(e) => setRegionFilter(e.target.value)}
                className="text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white"
                data-testid="region-filter"
              >
                <option value="all">All Regions</option>
                {availableRegions.map(r => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
            </div>
            
            <Button variant="outline" size="sm" onClick={fetchLocations} disabled={loading}>
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700">
            {error}
          </div>
        )}

        {/* Locations Table */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="locations-table">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <SortableHeader column="name" label="Name" />
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider">Slug</th>
                  <SortableHeader column="region" label="Region" />
                  <SortableHeader column="type" label="Type" />
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loading ? (
                  <tr>
                    <td colSpan="5" className="px-4 py-12 text-center text-slate-500">
                      <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
                      Loading locations...
                    </td>
                  </tr>
                ) : filteredLocations.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="px-4 py-12 text-center text-slate-500">
                      No locations found
                    </td>
                  </tr>
                ) : (
                  filteredLocations.map((location) => (
                    <tr key={location.slug} className="hover:bg-slate-50" data-testid={`location-row-${location.slug}`}>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center text-slate-600">
                            {getTypeIcon(location.type)}
                          </div>
                          <div>
                            <div className="font-medium text-slate-900">{location.name}</div>
                            {location.subtitle && (
                              <div className="text-xs text-slate-500">{location.subtitle}</div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <code className="text-sm text-slate-600 bg-slate-100 px-2 py-1 rounded">
                          {location.slug}
                        </code>
                      </td>
                      <td className="px-4 py-4">
                        {getRegionBadge(location.region)}
                      </td>
                      <td className="px-4 py-4">
                        {getTypeBadge(location.type)}
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openEditModal(location)}
                            data-testid={`edit-btn-${location.slug}`}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="location-modal">
          <div className="bg-white rounded-2xl max-w-lg w-full p-6 shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-bold">
                {editingLocation ? "Edit Location" : "Add New Location"}
              </h3>
              <button onClick={() => setShowModal(false)} className="text-slate-400 hover:text-slate-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            {formError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 flex items-center gap-2 text-red-700 text-sm">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                {formError}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="Kalamata Airport"
                    required
                    data-testid="input-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="slug">Slug *</Label>
                  <Input
                    id="slug"
                    value={formData.slug}
                    onChange={(e) => setFormData({...formData, slug: e.target.value})}
                    placeholder="kalamata-airport"
                    required
                    disabled={!!editingLocation}
                    className={editingLocation ? "bg-slate-50" : ""}
                    data-testid="input-slug"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="subtitle">Subtitle</Label>
                <Input
                  id="subtitle"
                  value={formData.subtitle}
                  onChange={(e) => setFormData({...formData, subtitle: e.target.value})}
                  placeholder="KLX - Kalamata International"
                  data-testid="input-subtitle"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="type">Type *</Label>
                  <select
                    id="type"
                    value={formData.type}
                    onChange={(e) => setFormData({...formData, type: e.target.value})}
                    className="w-full h-10 px-3 border border-slate-200 rounded-md bg-white"
                    data-testid="input-type"
                  >
                    {LOCATION_TYPES.map(t => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="region">Region {!editingLocation && '*'}</Label>
                  {editingLocation ? (
                    <>
                      <Input
                        id="region"
                        value={formData.region}
                        disabled
                        className="bg-slate-50 text-slate-500"
                        data-testid="input-region"
                      />
                      <p className="text-xs text-slate-400">Region cannot be changed after creation</p>
                    </>
                  ) : (
                    <select
                      id="region"
                      value={formData.region}
                      onChange={(e) => setFormData({...formData, region: e.target.value})}
                      className="w-full h-10 px-3 border border-slate-200 rounded-md bg-white"
                      data-testid="input-region"
                    >
                      {REGIONS.map(r => (
                        <option key={r} value={r}>{r}</option>
                      ))}
                    </select>
                  )}
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => setShowModal(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={saving} data-testid="save-location-btn">
                  {saving ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      {editingLocation ? "Update" : "Create"}
                    </>
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
